import { Capacitor, registerPlugin } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { HapticPattern, HAPTIC_MAPPING, HAPTIC_STRENGTH } from "./haptics_constants.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { isIOSWebWithoutNativeHaptics } from "../utils/runtime_platform.js";
import { SimulationContext } from "../simulation/simulation_context.js";
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";

// Delivery path actually used for a given vibrate() call.
export type HapticDeliveryPath =
  | "android_strong"    // native StrongHaptics plugin (duration+amplitude)
  | "android_fallback"  // StrongHaptics unavailable/rejected -> @capacitor/haptics
  | "ios_impact"        // @capacitor/haptics ImpactStyle
  | "telegram"          // Telegram WebApp.HapticFeedback
  | "web_vibrate"        // navigator.vibrate
  | "visual_fallback"    // iOS web without any vibration API - screen "shake"
  | "unsupported";       // nothing available at all

export type HapticSkipReason =
  | "simulation"        // headless bot run
  | "disabled"          // user turned haptics off
  | "context_filtered"  // vibration_mode excludes this VibrationContext
  | "rate_limited";      // MIN_VIBRATION_INTERVAL / MAX_VIBRATIONS_PER_SECOND

export interface HapticLogEntry
{
  t: number;
  pattern: string;
  context: VibrationContext;
  outcome: "ok" | "skipped" | "error";
  path?: HapticDeliveryPath;
  skip_reason?: HapticSkipReason;
  duration?: number;
  amplitude?: number;
  error?: string;
}

export interface HapticSupportBreakdown
{
  platform: string;
  is_native: boolean;
  has_capacitor_haptics: boolean;
  has_telegram_haptics: boolean;
  has_web_vibrate: boolean;
  supported: boolean;
  android_strong_haptics_ok: boolean | null;
  android_has_amplitude_control: boolean | null;
}

export enum VibrationMode
{
  COMBO_ONLY = 0,
  PREVIEW_ONLY = 1,
  FULL = 2
}

export enum VibrationContext
{
  COMBO = "combo",
  PREVIEW = "preview",
  GENERAL = "general"
}

interface StrongHapticsPlugin
{
  vibrate( options: { duration: number; amplitude: number } ): Promise<void>;
  getCapabilities(): Promise<{ has_amplitude_control: boolean }>;
}

const StrongHaptics = registerPlugin<StrongHapticsPlugin>( "StrongHaptics" );

// The settings slider only offers these 5 stops (0/25/50/75/100%) - a full continuous
// range is more precision than anyone can actually feel, and forces every platform's
// coarser approximations (Telegram/iOS style buckets, web duration scaling) to work
// off a value nobody can reliably reproduce twice. Shared here so the slider UI and
// the controller agree on the exact same stops, and so any legacy continuous value
// from before this change gets migrated to the nearest stop on load.
export const HAPTICS_STRENGTH_STEPS: readonly number[] = [ 0, 0.25, 0.5, 0.75, 1 ];

export function snapToNearestHapticStep( value: number ): number
{
  let nearest = HAPTICS_STRENGTH_STEPS[0];
  let best_diff = Infinity;

  for ( const step of HAPTICS_STRENGTH_STEPS )
  {
    const diff = Math.abs( step - value );
    if ( diff < best_diff ) { best_diff = diff; nearest = step; }
  }

  return nearest;
}

function clampStrength( v: number ): number
{
  return Number.isFinite( v ) ? snapToNearestHapticStep( v ) : 0.5;
}

export class HapticsController
{
  public is_haptics_enabled: boolean = true;
  public vibration_mode: VibrationMode = VibrationMode.FULL;
  private strength: number = 1;
  private last_vibration_time: number = 0;
  private readonly MIN_VIBRATION_INTERVAL: number = 50;
  private readonly MAX_VIBRATIONS_PER_SECOND: number = 10;
  private vibration_count: number = 0;
  private vibration_window_start: number = 0;
  private visual_fallback_active: boolean = false;

  private static readonly LOG_LIMIT = 40;
  private log: HapticLogEntry[] = [];
  private skip_counts: Record<HapticSkipReason, number> = {
    simulation: 0, disabled: 0, context_filtered: 0, rate_limited: 0,
  };
  private android_strong_haptics_ok: boolean | null = null;
  // null = not queried yet. false is the "cheap motor, amplitude request silently
  // ignored" case getNativeStrength() has to work around by scaling duration instead.
  private android_has_amplitude_control: boolean | null = null;
  private android_capabilities_query: Promise<void> | null = null;
  private support_logged = false;

  constructor()
  {
    const stats = StatisticsStorage.load();
    this.is_haptics_enabled = stats.haptics_enabled ?? true;
    this.strength = clampStrength( stats.haptics_strength ?? 0.5 );
  }

  private pushLog( entry: Omit<HapticLogEntry, "t"> ): void
  {
    const full: HapticLogEntry = { t: Date.now(), ...entry };
    this.log.push( full );
    while ( this.log.length > HapticsController.LOG_LIMIT ) this.log.shift();

    if ( entry.outcome === "skipped" && entry.skip_reason )
      this.skip_counts[entry.skip_reason]++;

    if ( entry.outcome === "error" )
      CrashBreadcrumbs.add( "app", `haptics_error pattern=${entry.pattern} ctx=${entry.context} path=${entry.path ?? "?"} ${entry.error ?? ""}` );
  }

  public getLog(): readonly HapticLogEntry[]
  {
    return this.log.slice();
  }

  public getSkipCounts(): Readonly<Record<HapticSkipReason, number>>
  {
    return { ...this.skip_counts };
  }

  public clearLog(): void
  {
    this.log = [];
    this.skip_counts = { simulation: 0, disabled: 0, context_filtered: 0, rate_limited: 0 };
  }

  public getSupportBreakdown(): HapticSupportBreakdown
  {
    const has_capacitor_haptics = Capacitor.isNativePlatform();
    const has_telegram_haptics = this.isTelegramHapticsAvailable();
    const has_web_vibrate = typeof navigator.vibrate === "function";

    const breakdown: HapticSupportBreakdown = {
      platform: Capacitor.getPlatform(),
      is_native: Capacitor.isNativePlatform(),
      has_capacitor_haptics,
      has_telegram_haptics,
      has_web_vibrate,
      supported: has_capacitor_haptics || has_telegram_haptics || has_web_vibrate,
      android_strong_haptics_ok: this.android_strong_haptics_ok,
      android_has_amplitude_control: this.android_has_amplitude_control,
    };

    if ( !this.support_logged )
    {
      this.support_logged = true;
      CrashBreadcrumbs.add( "app", `haptics_support ${JSON.stringify( breakdown )}` );
    }

    return breakdown;
  }

  private isIOSWebWithoutNativeHaptics(): boolean
  {
    return isIOSWebWithoutNativeHaptics();
  }

  private isTelegramHapticsAvailable(): boolean
  {
    if ( !TelegramAPISettings.isTelegramEnvironment() )
    {
      return false;
    }

    const webapp: any = ( window as any )?.Telegram?.WebApp;
    if ( !webapp?.HapticFeedback?.impactOccurred )
    {
      return false;
    }

    if ( typeof webapp.isVersionAtLeast === "function" )
    {
      return webapp.isVersionAtLeast( "6.1" );
    }

    const version = parseFloat( String( webapp.version || "0" ) );
    return Number.isFinite( version ) && version >= 6.1;
  }

  private checkHapticSupport(): boolean
  {
    const has_capacitor_haptics = Capacitor.isNativePlatform();
    const has_telegram_haptics: boolean = this.isTelegramHapticsAvailable();
    const has_web_vibrate: boolean = typeof navigator.vibrate === "function";

    return has_capacitor_haptics || has_telegram_haptics || has_web_vibrate;
  }

  private canVibrate(): boolean
  {
    const now: number = Date.now();

    if ( now - this.last_vibration_time < this.MIN_VIBRATION_INTERVAL )
    {
      return false;
    }

    if ( now - this.vibration_window_start >= 1000 )
    {
      this.vibration_count = 0;
      this.vibration_window_start = now;
    }

    if ( this.vibration_count >= this.MAX_VIBRATIONS_PER_SECOND )
    {
      return false;
    }

    return true;
  }

  // Web Vibration API has no amplitude control - duration is the only lever, so a
  // low strength gets a shorter buzz instead of a softer one. Floored so "Low" stays
  // perceptible rather than rounding away to ~0ms.
  private static readonly WEB_DURATION_STRENGTH_FLOOR = 0.35;

  private getFallbackDuration( pattern: HapticPattern | number[] ): number | number[]
  {
    const duration_scale = HapticsController.WEB_DURATION_STRENGTH_FLOOR
      + ( 1 - HapticsController.WEB_DURATION_STRENGTH_FLOOR ) * this.strength;

    if ( Array.isArray( pattern ) )
    {
      return pattern.map( ms => Math.round( ms * duration_scale ) );
    }

    return Math.round( HAPTIC_STRENGTH[pattern].duration * duration_scale );
  }

  private getSingleFallbackDuration( pattern: HapticPattern | number[] ): number
  {
    const duration = this.getFallbackDuration( pattern );
    return Array.isArray( duration )
      ? duration.reduce( ( sum, value ) => sum + value, 0 )
      : duration;
  }

  // Queried once and cached: cheap ERM motors on some budget/older Android phones have
  // no amplitude control hardware at all, so VibrationEffect silently ignores whatever
  // amplitude we ask for and just runs at its one fixed intensity - confirmed on a
  // Redmi Note 8T via HAPTICS LOG, where every non-zero strength setting felt identical
  // even though the logged amplitude values were genuinely different each time. Once
  // known false, getNativeStrength() scales duration instead so strength still does
  // something on that hardware.
  private ensureAndroidCapabilitiesQueried(): Promise<void>
  {
    if ( this.android_capabilities_query ) return this.android_capabilities_query;

    this.android_capabilities_query = StrongHaptics.getCapabilities()
      .then( caps => { this.android_has_amplitude_control = caps.has_amplitude_control; } )
      .catch( () => { this.android_has_amplitude_control = true; } );

    return this.android_capabilities_query;
  }

  private getNativeStrength( pattern: HapticPattern | number[] ): { duration: number; amplitude: number }
  {
    const base = Array.isArray( pattern )
      ? { duration: this.getSingleFallbackDuration( pattern ), amplitude: HAPTIC_STRENGTH[HapticPattern.MEDIUM].amplitude }
      : HAPTIC_STRENGTH[pattern];

    if ( this.android_has_amplitude_control === false )
    {
      const scale = HapticsController.WEB_DURATION_STRENGTH_FLOOR
        + ( 1 - HapticsController.WEB_DURATION_STRENGTH_FLOOR ) * this.strength;

      return {
        duration: Math.max( 1, Math.round( base.duration * scale ) ),
        amplitude: 255, // ignored by this hardware either way
      };
    }

    return {
      duration: base.duration,
      amplitude: Math.round( base.amplitude * this.strength ),
    };
  }

  private getImpactStyle( pattern: HapticPattern | number[] ): ImpactStyle
  {
    if ( Array.isArray( pattern ) ) return ImpactStyle.Medium;

    switch ( pattern )
    {
      case HapticPattern.LIGHT: return ImpactStyle.Light;
      case HapticPattern.HEAVY: return ImpactStyle.Heavy;
      default: return ImpactStyle.Medium;
    }
  }

  // Telegram's HapticFeedback.impactOccurred also only takes a style string.
  private getTelegramStyle( pattern: HapticPattern | number[] ): string
  {
    const base = typeof pattern === "string" ? pattern : "medium";

    if ( this.strength <= 1 / 3 ) return "light";
    if ( this.strength <= 2 / 3 && base === "heavy" ) return "medium";
    return base;
  }

  private getVisualFallbackConfig( pattern: HapticPattern | number[] ): { distance: number; duration: number }
  {
    if ( Array.isArray( pattern ) )
    {
      return { distance: 8, duration: 180 };
    }

    switch ( pattern )
    {
      case HapticPattern.LIGHT:
        return { distance: 4, duration: 110 };
      case HapticPattern.MEDIUM:
        return { distance: 7, duration: 140 };
      case HapticPattern.HEAVY:
        return { distance: 10, duration: 180 };
      default:
        return { distance: 5, duration: 120 };
    }
  }

  private triggerVisualFallback( pattern: HapticPattern | number[] ): void
  {
    if ( this.visual_fallback_active )
    {
      return;
    }

    const root = document.body;
    if ( !root || typeof root.animate !== "function" )
    {
      return;
    }

    const { distance, duration } = this.getVisualFallbackConfig( pattern );
    this.visual_fallback_active = true;

    const animation = root.animate(
      [
        { transform: "translate3d(0, 0, 0) scale(1)" },
        { transform: `translate3d(${distance}px, 0, 0) scale(1.002)` },
        { transform: `translate3d(${-distance}px, 0, 0) scale(0.998)` },
        { transform: `translate3d(${Math.round( distance * 0.5 )}px, 0, 0) scale(1.001)` },
        { transform: "translate3d(0, 0, 0) scale(1)" }
      ],
      {
        duration,
        easing: "ease-out"
      }
    );

    animation.onfinish = () =>
    {
      this.visual_fallback_active = false;
      root.style.transform = "";
    };

    animation.oncancel = () =>
    {
      this.visual_fallback_active = false;
      root.style.transform = "";
    };
  }

  public async vibrate( pattern: HapticPattern | number[], context: VibrationContext = VibrationContext.GENERAL ): Promise<void>
  {
    const pattern_label = Array.isArray( pattern ) ? `[${pattern.join( "," )}]` : pattern;

    if ( SimulationContext.enabled ) return;

    if ( !this.is_haptics_enabled )
    {
      this.pushLog( { pattern: pattern_label, context, outcome: "skipped", skip_reason: "disabled" } );
      return;
    }
    if ( !this.shouldVibrateForContext( context ) )
    {
      this.pushLog( { pattern: pattern_label, context, outcome: "skipped", skip_reason: "context_filtered" } );
      return;
    }
    if ( !this.canVibrate() )
    {
      this.pushLog( { pattern: pattern_label, context, outcome: "skipped", skip_reason: "rate_limited" } );
      return;
    }

    const now: number = Date.now();
    this.last_vibration_time = now;
    this.vibration_count++;

    try
    {
      const platform = Capacitor.getPlatform();
      if ( platform === "android" )
      {
        await this.ensureAndroidCapabilitiesQueried();
        const strength = this.getNativeStrength( pattern );
        try
        {
          await StrongHaptics.vibrate( strength );
          this.android_strong_haptics_ok = true;
          this.pushLog( { pattern: pattern_label, context, outcome: "ok", path: "android_strong", duration: strength.duration, amplitude: strength.amplitude } );
        }
        catch ( error )
        {
          this.android_strong_haptics_ok = false;
          console.warn( "[Haptics] StrongHaptics unavailable, using Capacitor fallback", error );
          await Haptics.vibrate( { duration: strength.duration } );
          this.pushLog( { pattern: pattern_label, context, outcome: "ok", path: "android_fallback", duration: strength.duration, error: String( error ) } );
        }
        return;
      }

      if ( platform === "ios" )
      {
        await Haptics.impact( { style: this.getImpactStyle( pattern ) } );
        this.pushLog( { pattern: pattern_label, context, outcome: "ok", path: "ios_impact" } );
        return;
      }

      const webapp: any = ( window as any )?.Telegram?.WebApp;
      const telegram = webapp?.HapticFeedback;
      if ( this.isTelegramHapticsAvailable() && telegram?.impactOccurred )
      {
        telegram.impactOccurred( this.getTelegramStyle( pattern ) );
        this.pushLog( { pattern: pattern_label, context, outcome: "ok", path: "telegram" } );
        return;
      }

      if ( typeof navigator.vibrate === "function" )
      {
        const duration = this.getFallbackDuration( pattern );
        navigator.vibrate( duration );
        this.pushLog( { pattern: pattern_label, context, outcome: "ok", path: "web_vibrate", duration: Array.isArray( duration ) ? duration.reduce( ( a, b ) => a + b, 0 ) : duration } );
        return;
      }

      if ( this.isIOSWebWithoutNativeHaptics() )
      {
        this.triggerVisualFallback( pattern );
        this.pushLog( { pattern: pattern_label, context, outcome: "ok", path: "visual_fallback" } );
        return;
      }

      this.pushLog( { pattern: pattern_label, context, outcome: "error", path: "unsupported", error: "no vibration API available" } );
    }
    catch ( error )
    {
      console.error( "[Haptics] Vibration failed:", error );
      this.pushLog( { pattern: pattern_label, context, outcome: "error", error: String( error ) } );
    }
  }

  public async vibrateForSound( sound_clip: string ): Promise<void>
  {
    const haptic_pattern = HAPTIC_MAPPING[sound_clip];

    if ( haptic_pattern )
      await this.vibrate( haptic_pattern, VibrationContext.GENERAL );
  }

  private shouldVibrateForContext( context: VibrationContext ): boolean
  {
    switch ( this.vibration_mode )
    {
      case VibrationMode.COMBO_ONLY:
        return context === VibrationContext.COMBO;
      case VibrationMode.PREVIEW_ONLY:
        return context === VibrationContext.PREVIEW;
      case VibrationMode.FULL:
        return true;
      default:
        return false;
    }
  }

  public enableHaptics( status: boolean ): void
  {
    this.is_haptics_enabled = status;
    // The in-game quick-toggle only ever flips this flag, not strength. If the big
    // slider was dragged all the way to 0 (which also disables), re-enabling here
    // would otherwise leave amplitude at 0 - "on" but silently mute forever.
    if ( status && this.strength === 0 )
      this.strength = 0.5;
    StatisticsStorage.update( { haptics_enabled: status, haptics_strength: this.strength } );
  }

  public isHapticsEnabled(): boolean
  {
    return this.is_haptics_enabled;
  }

  public getStrength(): number
  {
    return this.strength;
  }

  // Live update while the settings slider is being dragged: affects amplitude and
  // gating immediately (so the drag preview buzzes correctly) but is NOT persisted -
  // that only happens once, in setStrength(), when the drag ends. Avoids a full
  // StatisticsStorage read-modify-write on every touchmove tick.
  public previewStrength( value: number ): void
  {
    this.strength = clampStrength( value );
    this.is_haptics_enabled = this.strength > 0;
  }

  public setStrength( value: number ): void
  {
    this.previewStrength( value );
    StatisticsStorage.update( { haptics_strength: this.strength, haptics_enabled: this.is_haptics_enabled } );
  }

  public isHapticsSupported(): boolean
  {
    return this.checkHapticSupport();
  }

  public setVibrationMode( mode: VibrationMode ): void
  {
    this.vibration_mode = mode;
  }

  public getVibrationMode(): VibrationMode
  {
    return this.vibration_mode;
  }

  public async vibratePreview(): Promise<void>
  {
    await this.vibrate( HapticPattern.LIGHT, VibrationContext.PREVIEW );
  }
}

export const hapticsController = new HapticsController();
